<template>
  <div class="relative">
    <!-- User Icon Button -->
    <button
      @click="toggleMenu"
      class="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
      :aria-expanded="isOpen"
      aria-haspopup="true"
    >
      <span class="text-lg font-semibold">
        {{ userInitial }}
      </span>
    </button>

    <!-- Dropdown Menu -->
    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isOpen"
        class="absolute right-0 mt-2 w-64 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
        role="menu"
        aria-orientation="vertical"
      >
        <!-- User Info Section -->
        <div class="px-4 py-3 border-b border-gray-100">
          <p class="text-sm font-medium text-gray-900 truncate">
            {{ user?.email || 'User' }}
          </p>
          <p class="text-xs text-gray-500 mt-0.5">
            {{ $t('userMenu.signedIn') }}
          </p>
        </div>

        <!-- Menu Items -->
        <div v-if="!user?.social_login" class="py-1">
          <button
            @click="handleChangePassword"
            class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            role="menuitem"
          >
            <span class="text-lg">🔑</span>
            <span>{{ $t('userMenu.changePassword') }}</span>
          </button>
        </div>

        <!-- Language Switcher -->
        <div class="border-t border-gray-100"></div>
        <div class="py-1">
          <div class="px-4 py-2 flex items-center gap-2">
            <span class="text-lg">🌐</span>
            <span class="text-sm text-gray-700 flex-1">{{ $t('language.label') }}</span>
            <button
              v-for="lang in languages"
              :key="lang.code"
              @click="setLocale(lang.code)"
              :class="[
                'text-xs px-2 py-0.5 rounded-full border transition-colors',
                locale === lang.code
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'text-gray-500 border-gray-300 hover:border-primary-400 hover:text-primary-600'
              ]"
            >
              {{ lang.code.toUpperCase() }}
            </button>
          </div>
        </div>

        <div class="border-t border-gray-100"></div>

        <div class="py-1">
          <button
            @click="handleAbout"
            class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            role="menuitem"
          >
            <span class="text-lg">ℹ️</span>
            <span>{{ $t('about.contact') }}</span>
          </button>
        </div>

        <div class="border-t border-gray-100"></div>

        <div class="py-1">
          <button
            @click="handleLogout"
            class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
            role="menuitem"
          >
            <span class="text-lg">🚪</span>
            <span>{{ $t('common.logout') }}</span>
          </button>
        </div>
      </div>
    </transition>

    <!-- Backdrop for mobile -->
    <div
      v-if="isOpen"
      class="fixed inset-0 z-40"
      @click="closeMenu"
    ></div>

    <!-- Change Password Modal -->
    <ChangePasswordModal
      v-if="showChangePasswordModal"
      @close="showChangePasswordModal = false"
    />

    <!-- About Modal -->
    <AboutModal
      v-if="showAboutModal"
      @close="showAboutModal = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '../composables/useAuth'
import ChangePasswordModal from './ChangePasswordModal.vue'
import AboutModal from './AboutModal.vue'

const router = useRouter()
const { locale } = useI18n()
const { user, logout } = useAuth()

const languages = [
  { code: 'de' },
  { code: 'en' },
]

const setLocale = (lang) => {
  locale.value = lang
  localStorage.setItem('locale', lang)
}

const isOpen = ref(false)
const showChangePasswordModal = ref(false)
const showAboutModal = ref(false)

const userInitial = computed(() => {
  if (user.value?.email) {
    return user.value.email[0].toUpperCase()
  }
  return '?'
})

const toggleMenu = () => {
  isOpen.value = !isOpen.value
}

const closeMenu = () => {
  isOpen.value = false
}

const handleChangePassword = () => {
  closeMenu()
  showChangePasswordModal.value = true
}

const handleAbout = () => {
  closeMenu()
  showAboutModal.value = true
}

const handleLogout = () => {
  closeMenu()
  logout()
  router.push('/login')
}

// Close menu when clicking outside
const handleClickOutside = (event) => {
  if (isOpen.value) {
    const menu = event.target.closest('.relative')
    if (!menu) {
      closeMenu()
    }
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
